import React, {useCallback, useState} from "react"
import {useAuth} from "@/context/user";
import {FacilitySelector} from "@/components/paginated-selector/facility-selector";
import {Link, useNavigate} from "react-router-dom";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {apiV2} from "@/api/axiosConfig";
import {ConfirmationModal} from "@/components/modals/ConfirmationModal";
import {Action, ActionSelect} from "@/components/ui/ActionSelect";
import DeleteConfirmation from "@/features/sites/components/delete-confirmation";
import {useCopyID} from "@/lib/misc/useCopyID";

type Facility = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    address: {
        formatted: string;
    };
}

export function FacilityListPage() {
    const navigate = useNavigate();
    const {isSuperAdmin} = useAuth();
    const [refreshKey, setRefreshKey] = useState(0);
    const {copyID, isCopied} = useCopyID();

    function facilitiesUrl() {
        return window.location.pathname + window.location.search;
    }
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [facilityToDelete, setFacilityToDelete] = useState<Facility | null>(null);

    const handleDeleteClick = useCallback((facility: Facility) => {
        setFacilityToDelete(facility);
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!facilityToDelete) return;

        try {
            await apiV2.delete(`facilities/${facilityToDelete.id}`);
            setRefreshKey(prev => prev + 1);
            setFacilityToDelete(null);
        } catch (error) {
            console.error("Failed to delete facility:", error);
            alert("Failed to delete facility");
        }
    }, [facilityToDelete]);

    const handleColumnClick = useCallback((facility: Facility, columnIndex: number) => {
        // Column 0 is the facility name
        if (columnIndex === 0) {
           navigate(`/facilities/edit/${facility.id}?prev=${encodeURIComponent(facilitiesUrl())}`);
        }

        if(columnIndex === 1 && isSuperAdmin) {
            window.open(`/management/clients/edit/${facility.clientID}`, "_blank");
        }
    }, [isSuperAdmin]);

    const renderActions = useCallback((facility: Facility) => {

        return <ActionSelect label={"Select Action"}>
            <Action label={"Edit"} onSelect={() =>  navigate(`/facilities/edit/${facility.id}?prev=${encodeURIComponent(facilitiesUrl())}`)} />
            <Action label={"Delete"} onSelect={() => handleDeleteClick(facility)}/>
            <Action label={isCopied(facility.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(facility.id)} keepOpen />
        </ActionSelect>
    }, [handleDeleteClick, copyID, isCopied]);

    return (
        <div className="flex flex-col gap-4">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <span className="text-grey">Facilities</span>
            </nav>
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold">Facilities</h1>
                <div className="flex gap-2 flex-shrink-0">
                    <ButtonV2
                        label="Import Facilities"
                        onClick={() => navigate(`/facilities/import?prev=${encodeURIComponent(facilitiesUrl())}`)}
                    />
                    <ButtonV2
                        label="Create Facility"
                        onClick={() => navigate(`/facilities/create?prev=${encodeURIComponent(facilitiesUrl())}`)}
                    />
                </div>
            </div>
            <div className="">
                <FacilitySelector
                    key={refreshKey}
                    renderActions={renderActions}
                    onColumnClick={handleColumnClick}
                />
            </div>


            <ConfirmationModal
                open={deleteModalOpen}
                setOpen={setDeleteModalOpen}
                title="Delete Facility"
                message={`Are you sure you want to delete "${facilityToDelete?.name}"? This action cannot be undone.`}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                confirmButtonColor="red"
                onConfirm={handleDeleteConfirm}
            />
        </div>
    )
}
